import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent } from '@theme/events';

/**
 * Keeps a product card's "Add" / "Choose options" button in sync with swatch
 * selection. There is no modal here: "Add" submits the card's own form
 * directly, and "Choose options" is a plain link to the product page (kept
 * up to date with whichever variant the current swatch selection resolves
 * to) so picking remaining options happens on the product page itself.
 *
 * @typedef {object} QuickAddRefs
 * @property {HTMLAnchorElement} [chooseLink] - The "Choose options" link.
 *
 * @extends {Component<QuickAddRefs>}
 */

export class QuickAddComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
  }

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected,
   * and keeps the "Choose options" link pointed at the right variant.
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);

    if (this.refs.chooseLink) this.refs.chooseLink.href = this.productPageUrl;
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
